import React, { useEffect, useState, useRef } from 'react';
/**
 * TextHighlightRenderer — Select a word/segment from text.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */

import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
interface Segment {
  text: string;
  id: string;
  isCorrect?: boolean;
  isTappable?: boolean;
}
/** Build segments from raw text + correctHighlights when explicit segments are not provided. */
function buildSegmentsFromText(text: string, correctHighlights: Array<{
  start: number;
  end: number;
}>): Segment[] {
  if (!text) return [];
  const segments: Segment[] = [];
  const wordRegex = /\S+/g;
  let match: RegExpExecArray | null;
  while ((match = wordRegex.exec(text)) !== null) {
    const wordStart = match.index;
    const wordEnd = match.index + match[0].length;
    const isCorrect = correctHighlights.some(h => wordStart < h.end && wordEnd > h.start);
    segments.push({
      id: `word-${wordStart}`,
      text: match[0],
      isCorrect,
      isTappable: true
    });
  }
  return segments;
}
export function TextHighlightRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const segments: Segment[] = (() => {
    if (config.segments && config.segments.length > 0) return config.segments;
    if (config.text) return buildSegmentsFromText(config.text, config.correctHighlights || []);
    return [];
  })();
  const prevValidateRef = useRef(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    setSelectedId(null);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(selectedId !== null);
  }, [selectedId, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current && selectedId) {
      prevValidateRef.current = true;
      onAnswer(selectedId);
    }
  }, [shouldValidate, selectedId, onAnswer]);
  const handleKeyDown = (e: React.KeyboardEvent, index: number, segment: Segment) => {
    if (segment.isTappable === false) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelectedId(segment.id);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      let next = index + 1;
      while (next < segments.length && segments[next].isTappable === false) next++;
      if (next < segments.length) document.getElementById(`segment-${next}`)?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      let prev = index - 1;
      while (prev >= 0 && segments[prev].isTappable === false) prev--;
      if (prev >= 0) document.getElementById(`segment-${prev}`)?.focus();
    }
  };
  return <div className="w-full max-w-3xl mx-auto" data-id="element-7516">
      <div className="bg-white p-6 md:p-8 rounded-2xl border-2 border-slate-200 shadow-sm leading-loose text-lg md:text-xl" role="listbox" aria-label="Kies het juiste stukje tekst" data-id="element-7517">
        {segments.map((segment, index) => {
        const isSelected = selectedId === segment.id;
        if (segment.isTappable === false) {
          return <span key={segment.id || index} className="text-slate-800" data-id="element-7518">
                <LatexRenderer content={segment.text} data-id="element-7519" />
              </span>;
        }
        return <span key={segment.id || index} id={`segment-${index}`} role="option" aria-selected={isSelected} tabIndex={0} onClick={() => setSelectedId(segment.id)} onKeyDown={e => handleKeyDown(e, index, segment)} className={`inline-block px-1 rounded transition-colors cursor-pointer select-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${isSelected ? 'bg-indigo-600 text-white shadow-sm' : 'hover:bg-indigo-100 text-slate-800 border-b-2 border-transparent hover:border-indigo-200'}`} data-id="element-7520">
              <LatexRenderer content={segment.text} data-id="element-7521" />
            </span>;
      })}
      </div>
      <p className="text-center text-slate-500 text-sm mt-4" data-id="element-7522">
        Klik op tekst of gebruik de pijltjestoetsen
      </p>
    </div>;
}